import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { App, Button, List, Popconfirm, Tag } from 'antd';

import { familyIdsOf, referencedRecordPath } from '../../utils/collectionFamily';
import { normalizeId } from '../../utils/ids';
import { useOptionalCardDefinitions } from '../../contexts/CardDefinitionsContext';
import getItemDisplayValue from '../../utils/items';
import { createLookupResolver } from '../../utils/lookups';
import useLinkedItems from '../../hooks/useLinkedItems';
import useEnsureCards from '../../hooks/useEnsureCards';
import { DataState, Skeleton, deriveDataState } from '../loading';
import RecordBadge, { RecordIdentity } from '../common/RecordBadge';
import { CLOSE, DUPLICATE, RELATE } from '../icons';
import CopyReferencesModal from '../globalCollections/CopyReferencesModal';
import {
	RELATION_DIRECTION,
	RELATION_ORIGIN,
	createItemRelationship,
	deleteItemRelationship,
	fetchItemRelationships,
} from '../../services/itemRelationships';
import RelateItemModal from './RelateItemModal';
import useLinkedSources from '../../hooks/useLinkedSources';
import { useCollectionContext } from '../../contexts/CollectionContext';
import { canManage as canManageCollection } from '../../utils/permissions';
import { destructivePopconfirmProps } from '../common/confirmDestructive';

const EMPTY_CARDS = Object.freeze({});
const noElements = () => [];

/**
 * The "Related items" section at the bottom of an item form.
 *
 * Reads and writes immediately rather than deferring to the form's
 * Save button — relationships are their own records, not item fields.
 *
 * `derived` rows come from card-lookup element values and are read-only here;
 * the user removes them by clearing the element. `linked` rows are explicit and
 * removable. The two are intentionally not de-duplicated: an item related both
 * ways gets a row for each.
 *
 * `origins` picks which of the two to show, and every surface today shows
 * both — a derived row restates a lookup field the form also renders, but the
 * form saying "No related items" while the detail pane listed three was worse.
 * Counts follow the same filter.
 *
 * `variant='embedded'` drops the panel chrome and heading for surfaces that
 * frame the section themselves (the read view's meta accordion); `refreshKey`
 * reloads when bumped (an action menu related something elsewhere);
 * `onCountChange` reports the row count for a header badge; `onLoadedChange`
 * reports whether the first read for the record has landed, so a count that
 * is still the empty list's 0 is never read as an answer (the switcher's
 * first entry, KPR-719).
 */
const ALL_ORIGINS = Object.freeze([RELATION_ORIGIN.LINKED, RELATION_ORIGIN.DERIVED]);

export default function RelatedItems({
	itemId,
	collectionId,
	item,
	disabled = false,
	variant = 'panel',
	refreshKey = 0,
	onCountChange,
	onLoadedChange,
	origins = ALL_ORIGINS,
}) {
	const { message } = App.useApp();
	// Optional: the read view renders this section on surfaces that mount
	// without the definitions cache (tests, isolated panes); labels then fall
	// back to raw display values rather than the whole pane failing.
	const cardDefinitions = useOptionalCardDefinitions();
	const cardsById = cardDefinitions?.cardsById || EMPTY_CARDS;
	const getElementsForCard = cardDefinitions?.getElementsForCard || noElements;
	const cardsLoading = Boolean(cardDefinitions?.loading);

	const [allRelations, setAllRelations] = useState([]);
	const [itemsById, setItemsById] = useState({});
	// A related item of a card this session has not seen yet is fetched, not
	// left as a bare id.
	const relatedItems = useMemo(() => Object.values(itemsById), [itemsById]);
	useEnsureCards(relatedItems);
	// The origin filter is applied on read so a change of `origins` never
	// needs a refetch.
	const originsKey = (origins || ALL_ORIGINS).join('|');
	const relations = useMemo(() => {
		const allowed = new Set(originsKey.split('|'));
		return allRelations.filter(relation => allowed.has(relation.origin));
	}, [allRelations, originsKey]);
	const [loading, setLoading] = useState(false);
	// The record whose relationships are on screen, and whether the last read of them failed.
	const [loadedFor, setLoadedFor] = useState(null);
	const [loadError, setLoadError] = useState(false);
	const [attempt, setAttempt] = useState(0);
	const [saving, setSaving] = useState(false);
	const [pickerOpen, setPickerOpen] = useState(false);
	const mountedRef = useRef(false);
	const onCountChangeRef = useRef(onCountChange);
	onCountChangeRef.current = onCountChange;
	const onLoadedChangeRef = useRef(onLoadedChange);
	onLoadedChangeRef.current = onLoadedChange;

	const resolvedItemId = useMemo(() => {
		const normalized = normalizeId(itemId);
		return normalized && normalized !== '-1' ? normalized : null;
	}, [itemId]);

	useEffect(() => {
		mountedRef.current = true;
		return () => { mountedRef.current = false; };
	}, []);

	useEffect(() => {
		if (typeof onCountChangeRef.current === 'function') onCountChangeRef.current(relations.length);
	}, [relations.length]);

	const loadedForRecord = Boolean(resolvedItemId) && loadedFor === resolvedItemId;
	useEffect(() => {
		if (typeof onLoadedChangeRef.current === 'function') onLoadedChangeRef.current(loadedForRecord);
	}, [loadedForRecord]);

	// `signal` cancels a read the section no longer needs (the record moved
	// on, or the section unmounted): its answer must never land under the
	// next record, and a cancelled read is not a failure to report.
	const load = useCallback(async ({ withSpinner = true, signal } = {}) => {
		if (!resolvedItemId) {
			// An item that went away mid-read: the read was cancelled, so its
			// own `finally` will not stop the spinner.
			setAllRelations([]);
			setItemsById({});
			setLoading(false);
			return;
		}
		if (withSpinner) {
			setLoading(true);
			setLoadError(false);
		}
		try {
			const result = await fetchItemRelationships(resolvedItemId, { signal });
			if (!mountedRef.current || signal?.aborted) return;
			setAllRelations(result.relations);
			setItemsById(result.itemsById);
			setLoadedFor(resolvedItemId);
		} catch (error) {
			if (signal?.aborted) return;
			console.error(error);
			// A read the section is waiting on says so in its own place, with
			// Retry; a quiet re-read after a change has no place of its own.
			if (mountedRef.current) {
				if (withSpinner) setLoadError(true);
				else message.error('Unable to load related items.');
			}
		} finally {
			if (withSpinner && mountedRef.current && !signal?.aborted) setLoading(false);
		}
	}, [resolvedItemId, message]);

	useEffect(() => {
		const controller = new AbortController();
		load({ signal: controller.signal }).catch(() => {});
		return () => controller.abort();
	}, [load, refreshKey, attempt]);

	const linkedItemIds = useMemo(() => relations
		.filter(relation => relation.origin === RELATION_ORIGIN.LINKED)
		.map(relation => relation.itemId), [relations]);

	// The endpoint returns the far-side items, but a far-side item's own display
	// template may nest a lookup pointing anywhere — very commonly back at the
	// item being viewed, which the endpoint has no reason to include. Seed the
	// pool with what we already hold (far-side items plus the current item, which
	// also keeps unsaved edits to it visible here) and let useLinkedItems batch
	// fetch whatever is still missing; without that step those tokens render as
	// raw ids.
	const lookupSeed = useMemo(() => {
		const seed = Object.values(itemsById);
		if (item && normalizeId(item?._id)) seed.push(item);
		return seed;
	}, [itemsById, item]);

	const resolvedItemsById = useLinkedItems({
		items: lookupSeed,
		getElementsForCard,
		enabled: !cardsLoading,
		resetKey: resolvedItemId,
	});

	const resolveLookupValue = useMemo(() => createLookupResolver({
		itemsById: resolvedItemsById,
		cardsById,
		getElementsForCard,
	}), [resolvedItemsById, cardsById, getElementsForCard]);

	const rows = useMemo(() => relations.map(relation => {
		const target = resolvedItemsById[relation.itemId];
		const card = target ? cardsById[normalizeId(target.card_id)] : undefined;
		const label = target
			? getItemDisplayValue({
				card,
				elementDefs: getElementsForCard(target.card_id),
				item: target,
				resolveLookupValue,
			})
			: relation.itemId;
		return { relation, card, label };
	}), [relations, resolvedItemsById, cardsById, getElementsForCard, resolveLookupValue]);

	const handleRelate = useCallback(async (relatedItemId) => {
		if (!resolvedItemId) return;
		setSaving(true);
		try {
			await createItemRelationship({ itemId: resolvedItemId, relatedItemId });
			if (!mountedRef.current) return;
			setPickerOpen(false);
			message.success('Item related.');
			await load({ withSpinner: false });
		} catch (error) {
			console.error(error);
			if (!mountedRef.current) return;
			const status = error?.response?.status;
			if (status === 409) {
				message.warning('These items are already related.');
			} else if (status === 400) {
				message.error('Those items can\'t be related.');
			} else {
				message.error('Unable to relate that item.');
			}
		} finally {
			if (mountedRef.current) setSaving(false);
		}
	}, [resolvedItemId, load, message]);

	const handleUnrelate = useCallback(async (relationshipId) => {
		if (!resolvedItemId || !relationshipId) return;
		setSaving(true);
		try {
			await deleteItemRelationship({ itemId: resolvedItemId, relationshipId });
			if (!mountedRef.current) return;
			message.success('Item unrelated.');
			await load({ withSpinner: false });
		} catch (error) {
			console.error(error);
			if (mountedRef.current) message.error('Unable to remove that relationship.');
		} finally {
			if (mountedRef.current) setSaving(false);
		}
	}, [resolvedItemId, load, message]);

	const describeRelation = (relation) => {
		if (relation.origin === RELATION_ORIGIN.LINKED) return 'Added by a user';
		const elementLabel = relation.elementLabel || relation.elementName;
		if (!elementLabel) return 'From a card lookup';
		return relation.direction === RELATION_DIRECTION.INBOUND
			? `Referenced by ${elementLabel}`
			: `Via ${elementLabel}`;
	};

	const canManage = Boolean(resolvedItemId) && !disabled;
	// A far-side record lives in ITS OWN collection — a lookup can point into a
	// global collection (docs/plans/personal-discover-global.md §4.5) — so its
	// link, and the copy action, read the record's collection, not this one.
	const [copying, setCopying] = useState(null); // { itemId, collectionId, label }
	// Where the far side lives, when the answer says: the loaded record's
	// collection, else the relation's. Null when neither does — the badge is
	// then unqualified, never qualified with a guess.
	const knownCollectionOf = relation => normalizeId(itemsById[relation.itemId]?.collection_id) || normalizeId(relation.collection_id) || null;
	// ...and for the links and the copy action, which need somewhere to go,
	// the list's own collection when nothing says otherwise.
	const collectionOf = relation => knownCollectionOf(relation) || normalizeId(collectionId);
	// A far-side record a COLLECTION LINK shares (keepr-api § Lookups across a
	// link) is copied here only when that link lets this collection keep
	// copies; a global collection's, as before.
	const hasForeign = rows.some(({ relation }) => collectionOf(relation) !== normalizeId(collectionId));
	const linkedSources = useLinkedSources(collectionId, { enabled: hasForeign });
	// Copying is a MANAGER's act on this collection (POST …/copy-references
	// is manage-gated) — `canManage` above is only "may change this item's
	// relationships", which a writer has. The collection's myAccess answers;
	// outside its layout (no context for this collection) nothing is offered.
	const contextCollection = useCollectionContext()?.collection || null;
	const managesHere = normalizeId(contextCollection?._id) === normalizeId(collectionId) && canManageCollection(contextCollection);
	const copyAllowed = relation => {
		if (!managesHere) return false;
		// Not until the links are read: a copy the link forbids never flashes up.
		if (linkedSources.loading) return false;
		const source = linkedSources.sources.get(collectionOf(relation));
		return !source || source.allowCopies;
	};
	// SUB-COLLECTIONS: a far side in this collection's family (a parent's
	// record a sub-collection's item points at, a sub-collection's record) is
	// never a copy candidate — it is already this family's. A parent's record
	// the viewer cannot open there opens in this collection's list, read-only
	// (utils/collectionFamily referencedRecordPath).
	const familyContext = normalizeId(contextCollection?._id) === normalizeId(collectionId) ? contextCollection : null;
	const familyIds = familyContext ? familyIdsOf(familyContext) : null;
	const inFamily = relation => Boolean(familyIds && familyIds.has(collectionOf(relation)));
	const pathOf = relation => (familyContext && referencedRecordPath(familyContext, collectionOf(relation), relation.itemId))
		|| `/collections/${collectionOf(relation)}/items/${relation.itemId}`;
	const embedded = variant === 'embedded';
	const hasData = Boolean(resolvedItemId) && loadedFor === resolvedItemId;
	// Until the first read has begun there is nothing to say: never "No related items".
	const waiting = Boolean(resolvedItemId) && (loading || (!hasData && !loadError));
	const emptyLine = (
		<div className='empty-line'>
			{!resolvedItemId ? (
				<span>Save the item to relate others.</span>
			) : (
				<>
					<span>No related items</span>
					{canManage ? (
						<>
							<span aria-hidden='true'>·</span>
							<Button type='link' onClick={() => setPickerOpen(true)} disabled={saving}>
								Relate an item
							</Button>
						</>
					) : null}
				</>
			)}
		</div>
	);

	return (
		<div className={`related-items${embedded ? ' related-items--embedded' : ''}`}>
			<div className='related-items__header'>
				{embedded ? null : (
					<div className='micro-label related-items__title'>Related items</div>
				)}
				{/* With rows, the action rides the header; with none, the empty
				    line carries it inline (B3) — never both, never disabled. */}
				{canManage && rows.length ? (
					<Button
						type={embedded ? 'default' : 'link'}
						size={embedded ? 'small' : 'middle'}
						icon={embedded ? <RELATE /> : null}
						onClick={() => setPickerOpen(true)}
						disabled={loading || saving}
					>
						Relate item
					</Button>
				) : null}
			</div>

			<DataState
				className='related-items__state'
				state={deriveDataState({ loading: waiting, saving, error: loadError, hasData, isEmpty: !rows.length })}
				hasData={hasData}
				isEmpty={!rows.length}
				skeleton={<Skeleton variant='list-row' count={2} />}
				empty={emptyLine}
				error={loadError}
				onRetry={() => setAttempt(n => n + 1)}
				label='related items'
			>
				<List
					className='related-items__list'
					split={false}
					dataSource={rows}
					rowKey={row => row.relation.key}
					renderItem={({ relation, card, label }) => (
						<List.Item className='related-items__row'>
							<RecordIdentity
								className='related-items__identity'
								badge={(
									// A far side that lives in another collection than
									// the one this list is in wears it (the qualified
									// badge); one at home reads as ever.
									<RecordBadge
										kind='item'
										record={card}
										kicker={card?.label?.singular || card?.name || undefined}
										size='sm'
										title={label}
										to={pathOf(relation)}
										collection={knownCollectionOf(relation)}
									/>
								)}
								below={(
									<span className='related-items__row-meta'>
										<Tag color={relation.origin === RELATION_ORIGIN.LINKED ? 'blue' : 'default'}>
											{relation.origin === RELATION_ORIGIN.LINKED ? 'Linked' : 'Derived'}
										</Tag>
										<span>{describeRelation(relation)}</span>
									</span>
								)}
								actions={canManage && collectionOf(relation) !== normalizeId(collectionId) && !inFamily(relation) ? (copyAllowed(relation) ? (
									<Button
										type='text'
										size='small'
										icon={<DUPLICATE />}
										aria-label={`Copy ${label} into this collection`}
										title='Copy into this collection…'
										onClick={() => setCopying({ itemId: relation.itemId, collectionId: collectionOf(relation), label })}
									/>
								) : null) : relation.origin === RELATION_ORIGIN.LINKED && canManage ? (
									<Popconfirm
										title='Remove this relationship?'
										{...destructivePopconfirmProps('Remove')}
										onConfirm={() => handleUnrelate(relation.relationshipId)}
									>
										<Button
											type='text'
											size='small'
											danger
											disabled={saving}
											aria-label={`Remove relationship to ${label}`}
											title='Remove relationship'
											className='related-items__remove'
											icon={<CLOSE />}
										/>
									</Popconfirm>
								) : null}
							/>
						</List.Item>
					)}
				/>
			</DataState>

			{copying ? (
				<CopyReferencesModal
					open
					onClose={() => setCopying(null)}
					collectionId={normalizeId(collectionId)}
					source={{ collectionId: copying.collectionId, name: linkedSources.sources.get(copying.collectionId)?.source?.name }}
					variant={linkedSources.sources.has(copying.collectionId) ? 'linked' : 'global'}
					itemIds={[copying.itemId]}
					title={copying.label}
					onDone={() => { setCopying(null); load({ withSpinner: false }).catch(() => {}); }}
					onLinksStale={linkedSources.refresh}
				/>
			) : null}

			<RelateItemModal
				open={pickerOpen}
				onCancel={() => setPickerOpen(false)}
				onSelect={handleRelate}
				collectionId={collectionId}
				excludeItemId={resolvedItemId}
				linkedItemIds={linkedItemIds}
				saving={saving}
			/>
		</div>
	);
}
